import type { JourneyPage } from './types'

export const rambleToAction: JourneyPage = {
  slug: 'ramble-to-action',
  intro: [
    { text: "You're driving to a ", highlight: 'client meeting' },
    { text: 'A thought about another client ', highlight: 'pops in' },
    { text: 'You speak it into your ', highlight: 'watch' },
    { text: "By the time you park, it's ", highlight: 'done' },
    { text: 'This is ', highlight: 'WealthSpace' },
  ],
  colorClass: 'ramble',
  seo: {
    title: 'WealthSpace Ramble to Action | Voice Notes for Advisers',
    description:
      'Dictate voice notes via Apple Watch or CarPlay. AI transcription turns adviser thoughts into tasks, notes, and client follow-ups automatically.',
    keywords:
      'voice notes, AI transcription, adviser workflow, dictation, Apple Watch, CarPlay, financial adviser productivity, voice capture, Siri integration',
  },
  hero: {
    eyebrow: 'Picture this scenario',
    title: 'Speak it.',
    titleAccent: 'It happens.',
    subtitle:
      "Dictate a rambling thought into your Apple Watch or CarPlay while driving. By the time you park, it's become tasks, notes, and client follow-ups.",
    microcopy: 'Works with Apple Watch, CarPlay, Siri, and voice memos.',
  },
  journeySection: {
    label: 'The Story',
    title: 'Driving to the Hendersons.',
    titleAccent: 'Thinking out loud.',
    subtitle:
      "You're on the M4, heading to a client meeting. A thought strikes you about another client...",
  },
  timeline: [
    {
      time: '8:42 AM - On the motorway',
      icon: 'mic',
      iconColor: 'violet',
      title: 'The thought strikes',
      meta: '"Hey Siri, note to WealthSpace"',
      body: "You remember that Mrs Patterson mentioned her daughter is getting married next year. There might be some gift planning worth discussing. You tap your Apple Watch and start talking.",
      previewLabel: 'What you said',
      previewText:
        '"Just remembered Mrs Patterson mentioned her daughter\'s getting married next year - Sarah I think - need to call her about potential gift planning, maybe look at the annual exemption and see if there\'s anything we can do with the pension contributions too, also should probably review her risk profile while we\'re at it, it\'s been about 18 months..."',
    },
    {
      time: '8:43 AM - WealthSpace processes',
      icon: 'sparkle',
      iconColor: 'teal',
      title: 'AI understands context',
      meta: 'Background processing while you drive',
      body: 'WealthSpace transcribes your ramble, identifies the client (Mrs Patterson), extracts the key points, and figures out what you actually want to do about it.',
      previewLabel: 'AI interpretation',
      previewText:
        'Client: Mrs Patricia Patterson | Trigger: Daughter Sarah\'s wedding (next year) | Topics: Gift planning, annual exemption, pension contributions, risk profile review | Last review: 18 months ago',
    },
    {
      time: '8:44 AM - Actions created',
      icon: 'clipboard',
      iconColor: 'amber',
      title: 'Tasks generated',
      meta: 'Added to your task list automatically',
      body: "Your rambling thought has become three specific, actionable tasks - each linked to Mrs Patterson's client record with the right context attached.",
      previewLabel: 'Tasks created',
      previewText:
        "1. Schedule call with Mrs Patterson - discuss gift planning for Sarah's wedding | 2. Prepare annual exemption and pension contribution analysis | 3. Conduct attitude to risk review (last completed: June 2024)",
    },
    {
      time: '8:45 AM - Research prepared',
      icon: 'search',
      iconColor: 'emerald',
      title: 'Background research',
      meta: 'Ready for when you need it',
      body: "WealthSpace has already pulled Mrs Patterson's current holdings, the latest annual exemption rules, and a comparison of pension top-up options. It's sitting in your dashboard, waiting.",
      previewLabel: 'Research prepared',
      previewText:
        'Annual exemption 2025/26: £3,000 per donor (carry forward available) | Wedding gift exemption: £5,000 from parent | Pension annual allowance: £60,000 (unused from prior years: £22,400)',
    },
    {
      time: "9:15 AM - You arrive",
      icon: 'users',
      iconColor: 'sky',
      title: "You park at the Hendersons'",
      meta: '33 minutes since your ramble',
      body: 'You check your phone before the meeting. Three tasks created, research prepared, and a draft email to Mrs Patterson suggesting a call next week. You approve it with one tap.',
      previewLabel: 'Draft email ready',
      previewText:
        '"Dear Patricia, I was thinking about Sarah\'s upcoming wedding - congratulations again! There may be some useful planning opportunities around gifts and your pension that could be worth discussing..."',
    },
  ],
  result: {
    title: 'One ramble.',
    titleAccent: 'Everything organised.',
    body: "A 23-second voice note on the motorway became a complete client follow-up - tasks, research, and outreach - all before you turned off the engine.",
    stats: [
      { value: '23 sec', label: 'Time to capture the thought' },
      { value: '3', label: 'Tasks created automatically' },
      { value: '0', label: 'Things forgotten' },
    ],
  },
  features: {
    label: 'How it works',
    title: 'Capture thoughts',
    titleAccent: 'anywhere',
    subtitle:
      'WealthSpace meets you where you are - in the car, on a walk, wherever ideas strike.',
    items: [
      {
        icon: 'mic',
        title: 'Apple Watch',
        body: 'Raise your wrist, speak your thought. WealthSpace transcribes, interprets, and takes action before you lower your arm.',
      },
      {
        icon: 'users',
        title: 'CarPlay',
        body: '"Hey Siri, note to WealthSpace" - capture ideas while driving, safely and hands-free. Everything syncs by the time you park.',
      },
      {
        icon: 'document',
        title: 'Voice memos',
        body: 'Forward any voice memo to WealthSpace. Walking the dog, on the train, in between meetings - nothing gets lost.',
      },
    ],
  },
  testimonial: {
    quote:
      "The best ideas come at the worst times - driving, walking, between meetings. We built voice capture because advisers were losing brilliant thoughts to distraction. Your brain should be free to think. Let WealthSpace remember.",
    authorName: 'The WealthSpace Team',
    authorRole: 'Built by Advisers, for Advisers',
    initials: 'WS',
  },
  cta: {
    title: 'Never lose a thought',
    titleAccent: 'again',
    subtitle:
      "Book a demo and we'll show you how to capture ideas anywhere and turn them into action automatically.",
    note: 'Works with Apple Watch, iPhone, CarPlay, and Android.',
  },
}
